import logo from '@assets/img/logos/logo.png'
import { ListItemLink } from '@components/lists'
import Text from '@components/Text'
import { useAppSelector } from '@hooks/reduxHooks'
import { usePermissionsRole } from '@hooks/usePermissionsRole'
import { useCardReaderHealthCheck } from '@hooks/useServiceHealthCheck'
import {
  AdfScanner,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Print,
  PrintDisabled,
  Scanner,
} from '@mui/icons-material'
import LogoutIcon from '@mui/icons-material/Logout'
import {
  Box,
  Chip,
  Divider,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  Drawer as MuiDrawer,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { CSSObject, styled, Theme } from '@mui/material/styles'
import { AccessEnum, AccountRoleEnum, Member, SuperAdmin } from '@services/api'
import { logout } from '@state/thunks/authThunk'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { memberLinks } from '../containers/app/member/HomeMember'
import { superAdminLinks } from '../containers/app/super-admin/HomeSuperAdmin'
import FloatingMemberSituationSelect from './FloatingMemberSituationSelect'

const drawerWidth = 240

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
})

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(7)} + 1px)`,
  },
})

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0, 1),
  marginBottom: '20px',
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}))

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}))
type DrawerProps = {
  role: AccountRoleEnum
}

export function DrawerMenu({ role }: DrawerProps) {
  const dispatch = useDispatch()
  const { hasPermission } = usePermissionsRole()

  const hasPermissionToViewStats = hasPermission(AccessEnum.CAN_VIEW_STATS)

  const handleLogout = () => {
    dispatch(logout())
    //TODO: Clear all
  }

  return (
    <Drawer
      variant="permanent"
      id="app-drawer"
      PaperProps={{
        sx: {
          backgroundColor: '#3f3f3f',
        },
      }}
    >
      <DrawerHeader>
        <img src={logo} alt="logo" className="w-12 self-center" />
      </DrawerHeader>
      <Divider />

      <List>
        {role === AccountRoleEnum['super-admin'] &&
          superAdminLinks.map((link, key) => (
            <ListItemLink
              key={key}
              to={link.to}
              IconProps={{ className: link.icon }}
              text={link.title}
            />
          ))}
        {role === AccountRoleEnum.member &&
          memberLinks.map((link, key) => {
            if (link.to === '/m/stats' && !hasPermissionToViewStats) {
              return null
            }
            return (
              <ListItemLink
                key={key}
                to={link.to}
                IconProps={{ className: link.icon }}
                text={link.title}
                badgeContent={link.to === '/m/mss' ? 3 : undefined}
              />
            )
          })}
      </List>
      <Divider />
      <ListItem
        button
        sx={{ marginTop: 'auto', marginBottom: '50px' }}
        onClick={handleLogout}
      >
        <ListItemIcon>
          <LogoutIcon sx={{ marginRight: '20px', color: '#fff' }} />
          <Text text="logout" format color="#fff" />
        </ListItemIcon>
      </ListItem>
    </Drawer>
  )
}

export function BottomDrawerMenu({ user }: { user?: Member | SuperAdmin }) {
  const intl = useIntl()
  const { lastChecked } = useCardReaderHealthCheck()
  const [isVisible, setIsVisible] = useState(true)
  const {
    scannerState,
    printerState,
    cardReaderState,
    cardReaderInsertedCards,
  } = useAppSelector((state) => state.app)

  const { scanner } = useAppSelector((state) => ({
    scanner: state.scanner,
  }))
  const [cardReaderAnchorEl, setCardReaderAnchorEl] =
    useState<HTMLElement | null>(null)

  const handleCardReaderClick = (event: React.MouseEvent<HTMLElement>) => {
    setCardReaderAnchorEl(event.currentTarget)
  }

  const handleCardReaderClose = () => {
    setCardReaderAnchorEl(null)
  }

  const cardReaderPopoverOpen = Boolean(cardReaderAnchorEl)

  // Check if CPS or CPE card is inserted
  const hasProfessionalCard = cardReaderInsertedCards.some(
    (card) => card.type === 'CPS' || card.type === 'CPE',
  )

  const cardReaderColor = hasProfessionalCard ? '#2ecc71' : 'red'

  return (
    <Drawer
      variant="permanent"
      anchor="bottom"
      style={{
        position: 'relative',
      }}
      PaperProps={{
        sx: {
          left: '0',
          backgroundColor: '#3f3f3f',
          width: 'calc(100% - 0rem) !important',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: 'white',
          zIndex: 10000,
          gap: 2,
          px: 2,
          py: isVisible ? 0.5 : 0,
          height: isVisible ? 'auto' : '16px',
          minHeight: isVisible ? 'auto' : '16px',
          overflow: 'hidden',
          transition: (theme) =>
            theme.transitions.create(['height', 'min-height', 'padding'], {
              duration: theme.transitions.duration.standard,
              easing: theme.transitions.easing.easeInOut,
            }),
        },
      }}
    >
      {!isVisible ? (
        <Box
          onClick={() => setIsVisible(true)}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
          }}
        >
          <KeyboardArrowUp sx={{ color: 'white', fontSize: 16 }} />
        </Box>
      ) : (
        <>
          <FloatingMemberSituationSelect />
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-4">
              <Tooltip title={intl.formatMessage({ id: 'card-reader' })}>
                <div className="flex items-center gap-1">
                  <IconButton
                    onClick={handleCardReaderClick}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Fade in timeout={200}>
                      <AdfScanner style={{ color: cardReaderColor }} />
                    </Fade>
                  </IconButton>
                  {cardReaderInsertedCards.length > 0 && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.65rem',
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontWeight: 500,
                      }}
                    >
                      {cardReaderInsertedCards
                        .map((card) => card.type)
                        .join(' ')}
                    </Typography>
                  )}
                </div>
              </Tooltip>
              <Tooltip title={intl.formatMessage({ id: 'scanner' })}>
                <Fade in timeout={200}>
                  <Scanner
                    style={{
                      color: scanner.selectedScannerId ? 'green' : 'red',
                    }}
                  />
                </Fade>
              </Tooltip>
              <Tooltip title={intl.formatMessage({ id: 'printer' })}>
                <Fade in timeout={200}>
                  {printerState?.statusDescription === 'CONNECTED' ? (
                    <Print color="success" />
                  ) : (
                    <PrintDisabled style={{ color: cardReaderColor }} />
                  )}
                </Fade>
              </Tooltip>
            </div>
            <Typography variant="body1" color="white" fontWeight="medium">
              {user?.firstName} {user?.lastName}
            </Typography>
            <IconButton
              onClick={() => setIsVisible(false)}
              size="small"
              sx={{ padding: '4px', color: 'white' }}
            >
              <KeyboardArrowDown />
            </IconButton>
          </div>

          <Popover
            open={cardReaderPopoverOpen}
            anchorEl={cardReaderAnchorEl}
            onClose={handleCardReaderClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
          >
            <Box sx={{ p: 2, minWidth: 300, maxWidth: 400 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Cartes insérées ({cardReaderInsertedCards.length})
              </Typography>
              {cardReaderInsertedCards.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Aucune carte insérée
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {cardReaderInsertedCards.map((card, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 0.5 }}
                      >
                        <Chip
                          label={card.type}
                          size="small"
                          color={
                            card.type === 'CPS'
                              ? 'primary'
                              : card.type === 'CPE'
                              ? 'secondary'
                              : 'success'
                          }
                          sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                        />
                      </Stack>
                      {(card.firstName || card.lastName) && (
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {card.firstName} {card.lastName}
                          </Typography>
                          {card.type === 'CPS' && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              RPPS: {card.RPPS}
                            </Typography>
                          )}
                          {card.type === 'CPE' && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Organisation: {card.organisation}
                            </Typography>
                          )}
                          {card.type === 'CV' && card.dateOfBirth && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Né(e) le:{' '}
                              {new Date(card.dateOfBirth).toLocaleDateString(
                                'fr-FR',
                              )}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Popover>
        </>
      )}
    </Drawer>
  )
}
